import * as React from "react";
import {AccountResource} from "client/resources";
import {repository} from "clientInstance";
import { BoundSelect } from "components/form/Select/Select";
import isBound from "components/form/BoundField/isBound";
import Note from "components/form/Note/Note";
import { DoBusyTask } from "components/DataBaseComponent/DataBaseComponent";
import BusyRefreshContainer from "components/BusyRefreshContainer";
import {Icon, default as IconButton} from "components/IconButton/IconButton";
import InputWithActions from "components/InputWithActions";

export interface AzureCloudServiceSelectorProperties {
    "Octopus.Action.Azure.CloudServiceName": string;
    "Octopus.Action.Azure.StorageAccountName": string;
}

interface AzureCloudServiceSelectorProps {
    projectId: string;
    localNames: string[];
    isAccountBound: boolean;
    accountId: string;
    properties: AzureCloudServiceSelectorProperties;
    doBusyTask: DoBusyTask;
    getFieldError(field: string): string;
    setProperties(properties: Partial<AzureCloudServiceSelectorProperties>): void;
}

interface AzureCloudServiceSelectorState {
    account: AccountResource;
    cloudServices: any[];
    storageAccounts: any[];
    serviceIsBound: boolean;
    storageIsBound: boolean;
    busy: boolean; //TODO: move busy back out into props and use a HOC/Render prop component to manage this state
}

const toggleBusy = (value?: boolean) => (prev: AzureCloudServiceSelectorState, props: AzureCloudServiceSelectorProps) => ({...prev, busy: value ? value : !prev.busy});

export default class AzureCloudServiceSelector extends React.Component<AzureCloudServiceSelectorProps, AzureCloudServiceSelectorState> {

    constructor(props: any) {
        super(props);
        this.state = {
            account: null,
            cloudServices: [],
            storageAccounts: [],
            serviceIsBound: isBound(this.props.properties["Octopus.Action.Azure.CloudServiceName"], false),
            storageIsBound: isBound(this.props.properties["Octopus.Action.Azure.StorageAccountName"], false),
            busy: false
        };
    }

    componentDidMount() {
        if (!this.props.isAccountBound) {
            this.loadData();
        }
    }

    componentDidUpdate(prevProps: AzureCloudServiceSelectorProps, prevState: AzureCloudServiceSelectorState) {
        if (prevProps.accountId !== this.props.accountId) {
            this.loadData();
        }
    }

    componentWillReceiveProps(newprops: AzureCloudServiceSelectorProps) {
        if (newprops.isAccountBound && !this.state.serviceIsBound) {
            this.setState({serviceIsBound: true});
        }
        if (newprops.isAccountBound && !this.state.storageIsBound) {
            this.setState({storageIsBound: true});
        }
    }

    async loadData() {
        this.setState(toggleBusy(true));
        try {
            await this.props.doBusyTask(async () => {
                if (!this.props.isAccountBound && this.props.accountId) {
                    const account = await repository.Accounts.get(this.props.accountId);
                    const [cloudServices, storageAccounts] = await Promise.all([
                        repository.Accounts.getCloudServices(account),
                        repository.Accounts.getStorageAccounts(account)]);
                    this.setState({
                        account,
                        cloudServices: Object.keys(cloudServices).map(item => (cloudServices as any)[item]),
                        storageAccounts: Object.keys(storageAccounts).map(item => (storageAccounts as any)[item])
                    });
                } else {
                    this.setState({
                        account: null,
                        cloudServices: [],
                        storageAccounts: []
                    });
                }
            });
        } finally {
            this.setState(toggleBusy(false));
        }
    }

    render() {
        return <div>
            <div>
                <BusyRefreshContainer busy={this.state.busy}>
                    <InputWithActions input={
                        <BoundSelect
                            variableLookup={{
                                localNames: this.props.localNames,
                                projectId: this.props.projectId
                            }}
                            resetValue={this.props.properties["Octopus.Action.Azure.CloudServiceName"]}
                            isBound={this.state.serviceIsBound}
                            onIsBoundChanged={(value: boolean) => this.setState({serviceIsBound: this.props.isAccountBound || value})}
                            hideBindButton={this.props.isAccountBound}
                            value={this.props.properties["Octopus.Action.Azure.CloudServiceName"]}
                            onChange={(val) => this.props.setProperties({["Octopus.Action.Azure.CloudServiceName"]: val})}
                            items={this.state.cloudServices.map(item => ({ value: item.Name, text: item.Name + " - " + item.Location }))}
                            label="Cloud service"
                            allowClear={true}
                            allowFilter={true}
                            error={this.props.getFieldError("Octopus.Action.Azure.CloudServiceName")}
                        />}
                        actions={!this.props.isAccountBound && !this.state.serviceIsBound && <IconButton disabled={this.state.busy} onClick={() => this.loadData()} toolTipContent="Refresh" icon={Icon.Refresh}/>}
                    />
                </BusyRefreshContainer>
                <Note>The name of the cloud service to which files will be deployed.</Note>
            </div>
            <div>
                <BusyRefreshContainer busy={this.state.busy}>
                    <InputWithActions input={
                        <BoundSelect
                            variableLookup={{
                                localNames: this.props.localNames,
                                projectId: this.props.projectId
                            }}
                            resetValue={this.props.properties["Octopus.Action.Azure.StorageAccountName"]}
                            isBound={this.state.storageIsBound}
                            onIsBoundChanged={(value: boolean) => this.setState({storageIsBound: this.props.isAccountBound || value})}
                            hideBindButton={this.props.isAccountBound}
                            value={this.props.properties["Octopus.Action.Azure.StorageAccountName"]}
                            onChange={(val) => this.props.setProperties({["Octopus.Action.Azure.StorageAccountName"]: val})}
                            items={this.state.storageAccounts.map(item => ({ value: item.Name, text: item.Name + " - " + item.Location }))}
                            label="Storage account"
                            allowClear={true}
                            allowFilter={true}
                            error={this.props.getFieldError("Octopus.Action.Azure.StorageAccountName")}
                        />}
                        actions={!this.props.isAccountBound && !this.state.storageIsBound && <IconButton disabled={this.state.busy} onClick={() => this.loadData()} toolTipContent="Refresh" icon={Icon.Refresh}/>}
                />
                </BusyRefreshContainer>
                <Note>The name of a storage account that CSPKG files will be uploaded to before deployment.</Note>
            </div>
        </div>;
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./app/components/Actions/azureCloudService/AzureCloudServiceSelector.tsx